<template>
  <!-- Placeholder stays in document flow so scroll position, sibling layout, and
       editor selection rects behave as if the component lived here. Height is
       driven by the teleported element's measured size so the next in-flow
       sibling starts below the whole split. -->
  <div ref="placeholderRef" class="split-bg-placeholder" :style="{ height: placeholderHeight }">
    <!-- Teleport nested inside the placeholder so the component has a single
         root — otherwise the parent's fall-through attrs (data-id, data-type,
         …) can't be inherited and Vue warns. The Teleport still moves content
         to <body> regardless of where it's declared.

         Teleporting to <body> escapes the pipe base CSS wrapper (`body > #app`
         / `body > .body-content`) which applies max-width, padding, and `zoom`
         — all of which break `100vw` / `100vh` when used from inside. In body,
         our absolute positioning has no competing width/zoom context.

         `:disabled="!mounted"` keeps the teleport inactive during SSR and the
         first client render — otherwise SSG's prerenderer appends content to
         <body> but the client's first render expects it inline, which trips
         hydration. Mirrors the StickyContainer fix-to-page pattern. -->
    <Teleport to="body" :disabled="!mounted">
      <div
        ref="rootRef"
        class="split-bg"
        :class="{ 'split-bg--inline': !mounted }"
        :data-teleport-owner="teleportOwner"
        :style="mounted ? rootStyle : undefined"
      >
        <section
          v-if="showTop"
          ref="topSectionRef"
          class="split-bg__top"
          :class="{
            'split-bg__top--screen': heightMode === 'screen',
            'split-bg__top--content': heightMode === 'content',
          }"
          :style="topStyle"
        >
          <!-- .split-bg__content mirrors the page's root wrapper (body > #app)
             max-width + gutter padding, so slot content lines up with content
             placed directly on the page. Custom topPadding/bottomPadding
             layers on top for per-half breathing room. -->
          <div class="split-bg__content" :style="topContentStyle">
            <div class="split-bg__inner" :style="topInnerStyle">
              <!-- boundsEl lets a nested StickyContainer's `fix-to-bounds` mode
                   clamp to this section instead of the viewport/page — without
                   it, that mode would teleport past our own body-teleport and
                   escape to real body/viewport coordinates. -->
              <RenderSlot
                name="top"
                :children="slotChildren?.top"
                :editable="editable"
                :bounds-el="topSectionRef"
              >
                <div class="split-bg__empty">Drop content here (over image)</div>
              </RenderSlot>
            </div>
          </div>
        </section>
        <section
          v-if="showBottom"
          ref="bottomSectionRef"
          class="split-bg__bottom"
          :style="bottomStyle"
        >
          <div class="split-bg__content" :style="bottomContentStyle">
            <div class="split-bg__inner" :style="bottomInnerStyle">
              <RenderSlot
                name="bottom"
                :children="slotChildren?.bottom"
                :editable="editable"
                :bounds-el="bottomSectionRef"
              >
                <div class="split-bg__empty">Drop content here (plain color)</div>
              </RenderSlot>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick, useAttrs } from 'vue'
import type { CSSProperties } from 'vue'
import type { ComponentNode } from '@api/types.gen'
import { useImagePreset } from '@render/composables/useImagePreset'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useSlotHasVisibleChild } from '@render/composables/useSlotVisibility'
import { getColorStyleData, getSpacingStyleData } from '@render/composables/useStyleData'
import { effectiveZoom, viewportRect } from '@render/lib/pageZoom'
import RenderSlot from '@render/RenderSlot.vue'
import type { ImageHeightMode, SplitBackgroundFields } from './definition'

const props = withDefaults(
  defineProps<{
    fields?: SplitBackgroundFields
    slotChildren?: Record<string, ComponentNode[]>
    editable?: boolean
  }>(),
  {
    fields: () => ({}),
  },
)

// This component teleports its whole body to `<body>`, so the fall-through
// `data-id` stays on the 0-height placeholder left behind in the flow. Stamping
// the owning nodeId onto the moved root is what lets the editor's preview tools
// measure and photograph the real box. Same pattern as StickyContainer.
const attrs = useAttrs()
const teleportOwner = computed(() => (attrs['data-id'] as string | undefined) ?? undefined)

const placeholderRef = ref<HTMLElement | null>(null)
const rootRef = ref<HTMLElement | null>(null)
const topSectionRef = ref<HTMLElement | null>(null)
const bottomSectionRef = ref<HTMLElement | null>(null)

const rootStyle = ref<CSSProperties>({})
const placeholderHeight = ref<string>('0px')
// SSR-safe teleport gate: starts false so SSR output and the client's first
// render are identical (teleport disabled, no measured styles applied). Flipped
// in onMounted AFTER the first recalc so the teleport activates with the
// already-computed rootStyle in the same render batch.
const mounted = ref(false)

const imageProps = useImagePreset(() => props.fields?.image)
const desktopImageProps = useImagePreset(() => props.fields?.desktopImage)

const heightMode = computed<ImageHeightMode>(() => props.fields?.imageHeightMode ?? 'screen')

const pipe = usePipe()

const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

const topHasVisibleChild = useSlotHasVisibleChild(() => props.slotChildren?.top)
const bottomHasVisibleChild = useSlotHasVisibleChild(() => props.slotChildren?.bottom)

// Outside the editor a half with nothing visible in it — never filled, filled
// only with children its rules currently hide, or holding only a shared-node
// slot this instance left unfilled — is dropped entirely:
// `.split-bg__content`'s gutter padding alone would otherwise render it as a
// visible band (the image band keeps its image, the plain band its colour). The
// editor always renders both halves, or there would be nothing to drop into.
const showTop = computed(() => isEditor.value || topHasVisibleChild.value)
const showBottom = computed(() => isEditor.value || bottomHasVisibleChild.value)

// Editor-only: a half that still renders because we are in the editor, but
// whose children all resolve hidden, collapses its padding so the preview
// matches the built pipe's missing band. A half with no children at all keeps
// its padding — that box is the drop placeholder.
const topCollapsed = computed(() => !topHasVisibleChild.value && !!props.slotChildren?.top?.length)
const bottomCollapsed = computed(
  () => !bottomHasVisibleChild.value && !!props.slotChildren?.bottom?.length,
)

const topContentStyle = computed<CSSProperties>(() => (topCollapsed.value ? { padding: '0' } : {}))
const bottomContentStyle = computed<CSSProperties>(() =>
  bottomCollapsed.value ? { padding: '0' } : {},
)

const topStyle = computed<CSSProperties>(() => {
  const s: CSSProperties = {}
  if (imageProps.value) {
    // cssBackgroundFallback is a single `url(...)` — cssBackground builds an
    // `image-set(...)` from Nuxt Image's srcset, but Nuxt emits `Nw` descriptors
    // which aren't valid in CSS image-set, so the whole declaration gets dropped
    // and the image never renders. The fallback URL is what actually shows up.
    Object.assign(s, imageProps.value.cssBackgroundFallback)
    // Mirror the URL into a CSS variable so the desktop @media rule can fall
    // back to the mobile image when no desktopImage is set.
    s['--split-bg-mobile-image'] = imageProps.value.cssBackgroundFallback.backgroundImage
  }
  if (desktopImageProps.value) {
    s['--split-bg-desktop-image'] = desktopImageProps.value.cssBackgroundFallback.backgroundImage
  }
  s.backgroundSize = props.fields?.imageSize || 'cover'
  s.backgroundPosition = props.fields?.imagePosition || 'center'
  s.backgroundRepeat = 'no-repeat'
  s.backgroundAttachment = props.fields?.imageAttachment === 'fixed' ? 'fixed' : 'scroll'
  return s
})

const bottomStyle = computed<CSSProperties>(() => {
  const s: CSSProperties = {}
  const bg = getColorStyleData(props.fields?.bottomColor)
  if (bg) s.backgroundColor = bg
  return s
})

const topInnerStyle = computed<CSSProperties>(() => {
  const s: CSSProperties = {}
  if (topCollapsed.value) return s
  const padding = getSpacingStyleData(props.fields?.topPadding)
  if (padding) s.padding = padding
  return s
})

const bottomInnerStyle = computed<CSSProperties>(() => {
  const s: CSSProperties = {}
  if (bottomCollapsed.value) return s
  const padding = getSpacingStyleData(props.fields?.bottomPadding)
  if (padding) s.padding = padding
  return s
})

const recalc = () => {
  if (!placeholderRef.value || !rootRef.value) return

  // Find the body-level page wrapper (body > #app / body > .body-content) — the
  // stylesheet gives it `padding: var(--space-16)`, which creates a gap between
  // the page top and our placeholder's rect.top. When this SplitBackground is
  // the first thing on the page, we want the background to extend all the way
  // to y=0 (proper full-bleed). Otherwise, respect the flow position.
  let bodyChild: HTMLElement | null = placeholderRef.value
  while (bodyChild?.parentElement && bodyChild.parentElement !== document.body) {
    bodyChild = bodyChild.parentElement
  }

  let isAtPageTop = true
  let cursor: Element | null = placeholderRef.value
  while (cursor && cursor !== bodyChild) {
    if (cursor.previousElementSibling) {
      isAtPageTop = false
      break
    }
    cursor = cursor.parentElement
  }

  // Two px spaces meet here (see pageZoom.ts): the teleported root sits in the
  // unzoomed body, so it is positioned in viewport px, while the placeholder and
  // the wrapper's padding (getComputedStyle reports it unzoomed) are in the
  // page wrapper's zoomed px.
  const wrapperPaddingTop =
    bodyChild && isAtPageTop ? parseFloat(getComputedStyle(bodyChild).paddingTop) || 0 : 0
  const shiftPx = wrapperPaddingTop * (bodyChild ? effectiveZoom(bodyChild) : 1)

  const rect = viewportRect(placeholderRef.value)
  const topPx = Math.max(0, rect.top + window.scrollY - shiftPx)
  const leftPx = rect.left + window.scrollX

  rootStyle.value = {
    position: 'absolute',
    top: `${topPx}px`,
    left: `${leftPx}px`,
    width: '100vw',
    marginLeft: `${-leftPx}px`,
  }

  // Placeholder reserves flow space for the teleported content. When we shift
  // the teleport up by the wrapper's padding, the in-flow space needs to shrink
  // by the same amount so the next sibling starts at the teleport's bottom edge.
  // Both are viewport px; the placeholder's height is set in its own zoomed px.
  // The root's zoom is 1 once teleported, but not on the first pass before it.
  const contentHeight = rootRef.value.offsetHeight * effectiveZoom(rootRef.value)
  const placeholderZoom = effectiveZoom(placeholderRef.value)
  placeholderHeight.value = `${Math.max(0, (contentHeight - shiftPx) / placeholderZoom)}px`
}

let resizeObserver: ResizeObserver | null = null
let rafId: number | null = null

const scheduleRecalc = () => {
  if (rafId !== null) return
  rafId = requestAnimationFrame(() => {
    rafId = null
    recalc()
  })
}

onMounted(() => {
  // Required so the teleported child's `position: absolute` resolves against
  // the body (which has width = viewport width, no max-width, no zoom). Same
  // invariant StickyContainer's fix-to-page mode sets.
  document.body.style.position = 'relative'

  window.addEventListener('scroll', scheduleRecalc, { passive: true })
  window.addEventListener('resize', scheduleRecalc, { passive: true })

  resizeObserver = new ResizeObserver(scheduleRecalc)
  if (placeholderRef.value) resizeObserver.observe(placeholderRef.value)
  if (rootRef.value) resizeObserver.observe(rootRef.value)
  // Observe the body-level page wrapper too. When the active stylesheet changes
  // `--space-16`, `--md-container`, or anything else that touches the wrapper's
  // padding / max-width, the wrapper's size changes and we recompute
  // `wrapperPaddingTop` and reposition the teleport.
  let bodyChild: HTMLElement | null = placeholderRef.value
  while (bodyChild?.parentElement && bodyChild.parentElement !== document.body) {
    bodyChild = bodyChild.parentElement
  }
  if (bodyChild) resizeObserver.observe(bodyChild)

  nextTick(() => {
    recalc()
    // Populate rootStyle first, then flip the teleport gate: Vue batches these
    // two reactive updates into the same render pass, so the moment content
    // teleports into body it already has the measured absolute position — no
    // visible frame at top:0,left:0.
    mounted.value = true
  })
})

// Multi-source watcher: each getter is tracked and compared independently.
// `watch(() => [...], …)` (single getter returning an array) compares the
// array as a whole — fresh arrays are never equal, so the callback fires on
// every reactive trigger regardless of whether any tracked value changed.
watch(
  [
    () => props.fields?.imageHeightMode,
    () => props.fields?.imageSize,
    () => props.fields?.imagePosition,
    () => props.fields?.imageAttachment,
    () => props.fields?.image?.id,
    () => props.fields?.desktopImage?.id,
    () => props.fields?.bottomColor,
    () => props.fields?.topPadding,
    () => props.fields?.bottomPadding,
    showTop,
    showBottom,
  ],
  () => nextTick(recalc),
)

onBeforeUnmount(() => {
  window.removeEventListener('scroll', scheduleRecalc)
  window.removeEventListener('resize', scheduleRecalc)
  if (rafId !== null) cancelAnimationFrame(rafId)
  resizeObserver?.disconnect()
  resizeObserver = null
  // Leave body.position alone if another instance (or StickyContainer in
  // fix-to-page mode) still needs it — it's idempotent and the next mount
  // will re-apply.
  document.body.style.position = ''
})
</script>

<style scoped>
.split-bg-placeholder {
  display: block;
  width: 100%;
}

.split-bg {
  box-sizing: border-box;
  /* Pre-mount (SSR + first client render): CSS-only full-bleed so the inline
     render matches what the teleport produces after mount. Post-mount the
     inline style sets `position: absolute; marginLeft: …`, both of which win
     over these CSS properties, so this block becomes a no-op without needing
     to be removed. */
  position: relative;
  width: 100vw;
  margin-inline: calc(50% - 50vw);
}

/* Pre-mount the root is still inside the zoomed page wrapper, where viewport
   units shrink with the zoom (see pageZoom.ts) and the wrapper already applies
   the zoom its content needs. */
.split-bg--inline {
  width: calc(100vw / var(--app-zoom, 1));
  /* Per side, not `margin-inline`: see FullWidthContainer.vue (Safari < 15). */
  margin-left: calc(50% - 50vw / var(--app-zoom, 1));
  margin-right: calc(50% - 50vw / var(--app-zoom, 1));
}

.split-bg--inline .split-bg__top--screen {
  min-height: calc(100vh / var(--app-zoom, 1));
}

.split-bg--inline .split-bg__content {
  zoom: 1;
}

/* Pre-mount page-top escape: when the placeholder is the first child of its
   parent, the split-bg inline-rendered right after it needs to be pulled up by
   the page wrapper's top gutter (var(--space-16)) so the image reaches y=0,
   matching the JS-measured shift the teleport applies post-mount. Selector
   stops matching post-mount because the teleport moves .split-bg out of the
   placeholder's sibling chain. */
.split-bg-placeholder:first-child + .split-bg {
  margin-top: calc(-1 * var(--space-16));
}

.split-bg__top,
.split-bg__bottom {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.split-bg__top--screen {
  min-height: 100vh;
}

/* Desktop only: a touch device keeps the mobile image at any width, as it did when
   the old viewport meta reported the design width (render/lib/pageZoom.ts). */
@media (min-width: 700px) and (hover: hover), (min-width: 700px) and (pointer: fine) {
  .split-bg__top {
    background-image: var(--split-bg-desktop-image, var(--split-bg-mobile-image));
  }
}

.split-bg__content {
  box-sizing: border-box;
  width: 100%;
  max-width: var(--md-container);
  margin: 0 auto;
  padding: var(--space-16);
  /* Teleported out of the page wrapper, so the content re-applies its zoom. */
  zoom: var(--app-zoom);
}

.split-bg__inner {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
}

.split-bg__empty {
  min-height: 48px;
  border: 1.5px dashed #ccc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 0.7rem;
  padding: 8px;
  opacity: 0.7;
  user-select: none;
}
</style>

<!-- Unscoped: this has to target <html> directly. Reserves scrollbar width
     from the first paint so `100vw` and `50vw` don't jump when a classic
     desktop scrollbar appears after content measures. Mobile browsers use
     overlay scrollbars and ignore this rule, so no layout change there.

     `:has(.split-bg)` restricts the rule to pages that actually contain a
     SplitBackground — the component's CSS ships bundle-wide once any pipe
     page uses it, but this keeps the side-effect off pages that don't. -->
<style>
html:has(.split-bg) {
  scrollbar-gutter: stable;
}
</style>
